import { DocumentNode } from '@apollo/client';
import {
    Autocomplete,
    AutocompleteInputChangeReason,
    createFilterOptions,
    FilterOptionsState,
    ListItemText,
    TextField,
    TextFieldVariants
} from '@mui/material';
import { debounce } from 'lodash';
import React, { JSX, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ReferenceInput } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../auth/UserContext';
import { DEFAULT_WIDGET, FETCH_POLICY_CONFIG, INPUT_TYPES, TIMEOUT_DELAY, USER_ACTIONS } from '../constants/constants';
import { ENTITY_REFERENCE_LABEL } from '../constants/entityConstants';
import {
    entityToOption,
    isReferenceOptionEqualToValue,
    optionToEntity,
    optionToReference,
    ReferenceOption
} from '../form/forms';
import ChipOptionsRenderer, { RendererType } from '../form/widgets/ChipOptionsRenderer';
import { useI18n } from '../i18n/I18n';
import { searchClient, slimSearchQuery } from '../list/slimQuery';
import { useStyling } from '../Theme';
import { AIReferenceFieldStyling, checkForObject } from '../utils/commonUtils';
import AIAdornment from './AIAdornment';
import { useAutocompleteAIFlow } from './hooks/useAutocompleteAIFlow';
import Loading from './Loading';
import { SortableChips } from './SortableChipsComponents/SortableChips';
import {
    isReferenceOptionEqualById,
    makeGetChipKeys,
    replaceEditedChipSelection,
    replaceEditedChipWithOptionSelection
} from './utils/autocompleteChipResolutionUtils';
import { markMatch, resolveOptionLabel } from './utils/autocompleteLabelUtils';
import { autocompleteCustomRenderMultipleValues, withInactiveFilter } from './utils/autocompleteUtils';
import {
    deriveCreateSearchTerm,
    deriveFilterInputValue,
    deriveUserInputValue,
    generateCreateOption,
    getValue,
    hasExactLabelMatch,
    setDefaultValueById
} from './utils/autocompleteValueUtils';

export const AUTOCOMPLETE_MATCH_FIELDS = {
    email: 'email'
} as const;

export type AutocompleteMatchField = (typeof AUTOCOMPLETE_MATCH_FIELDS)[keyof typeof AUTOCOMPLETE_MATCH_FIELDS];

export type AutocompleteControllerProps = {
    name: string;
    entity: string;
    inputLabel: string;
    value: any;
    onChange: (name, value) => void;
    additionalFragmentName?: string;
    disabled: boolean;
    /**
     * Shows the current selection without letting it be changed: no chip delete, no option list.
     * Unlike `disabled` it leaves the field in its normal colours, for surfaces that report a
     * selection made elsewhere rather than one the user has yet to make.
     */
    readOnly?: boolean;
    required: boolean;
    hasErrors: boolean;
    errorText: string;
    id?: string;
    pageSize?: number;
    multiple?: boolean;
    filter?: any;
    allowFreeForm?: boolean;
    variant?: TextFieldVariants;
    size?: 'small' | 'medium' | undefined;
    type?: 'autocomplete' | 'chips' | 'radio';
    OptionRenderer?: (args: { option: any; isAIExtractedFlow?: boolean }) => JSX.Element;
    getOption?: (item) => void;
    onCreateHandle?: (value, save) => Promise<any>;
    addOption?: boolean;
    query?: DocumentNode;
    saveMutation?: Promise<any>;
    preload?: boolean;
    /**
     * Defers the initial non-AI option query until the field receives focus.
     * Only affects regular `preload={false}` flows; AI-extracted prefetches
     * still load before focus so AI resolution behavior remains unchanged.
     */
    loadOptionsWhenFocused?: boolean;
    filterById?: string[];
    defaultValueIds?: string[];
    sortBy?: string;
    disableIfOptionUnavailable?: boolean;
    allowDuplicateValue?: boolean;
    testId?: string;
    tooltip?: string | Function;
    queryTerm?: string;
    /** Wraps non-empty outgoing search terms in quotes to request an exact phrase match. */
    exactPhraseMatch?: boolean;
    /** Fields used for server-side and client-side matching. */
    matchFields?: AutocompleteMatchField[];
    isAIExtractedData?: boolean;
    requiresUserCorrection?: boolean;
    autoFocus?: boolean;
    skipReferenceCreationOnNoMatch?: boolean;
    returnObjectAsValue?: boolean;
    entityReferenceKey?: string;
    disableCloseOnSelect?: boolean;
    updateAIDataResolved?: (name: string) => void;
    autoSelectFirstOption?: boolean;
    /**
     * Maximum number of selections allowed when `multiple` is true
     * for the default autocomplete type (`type` is `undefined` or `'autocomplete'`).
     *
     * When `type` is set to `'chips'` or `'radio'`, this limit is currently
     * not enforced and the prop is ignored.
     *
     */
    maxSelectedCount?: number;
    filterSelectedOptions?: boolean;
    draggableChips?: boolean;
    fixedOptions?: string[];
    /**
     * The number of tags allowed to display
     * for the default autocomplete type (`type` is `undefined` or `'autocomplete'`).
     *
     * When `type` is set to `'chips'` or `'radio'`, this limit is currently
     * not enforced and the prop is ignored.
     */
    limitTags?: number;
    /**
     * @deprecated Use `returnObjectAsValue` and `entityReferenceKey` instead.
     */ notOnlyRef?: boolean;
    /**
     * Computes and returns the default option for the autocomplete control.
     *
     * This function is called with contextual data so callers can determine which
     * option (if any) should be selected when the component initializes or when
     * the input/context changes.
     *
     * @param items - An object containing contextual information used to derive the default option.
     *
     * @returns The option object to use as the default selection, or `undefined`/`null`
     *   if no suitable default could be determined.
     */
    getDefaultOption?: (items) => any;
    showInactive?: boolean;
};

const getOptionMatchFieldValue = (option: ReferenceOption, field: AutocompleteMatchField): string => {
    if (field === AUTOCOMPLETE_MATCH_FIELDS.email) {
        return typeof option.email === 'string' ? option.email : '';
    }

    return '';
};

const createOptionFilter = (matchFields: AutocompleteMatchField[]) =>
    createFilterOptions<ReferenceOption>({
        matchFrom: 'any', // Allows matching from any part of the string
        stringify: (option) =>
            `${resolveOptionLabel(option)} ${option.subLabel || ''} ${matchFields
                .map((field) => getOptionMatchFieldValue(option, field))
                .join(' ')}`
    });

type SanitizedPersonReferenceInput = {
    id?: string;
    label?: string;
    uriPrefix?: string;
};

const sanitizePersonReferenceInput = (
    referenceCandidate: unknown,
    fallbackLabel?: string
): SanitizedPersonReferenceInput | undefined => {
    let currentCandidate = referenceCandidate;

    for (let depth = 0; depth < 3; depth++) {
        if (!currentCandidate || typeof currentCandidate !== 'object') {
            return undefined;
        }

        const candidateWithReference = currentCandidate as {
            id?: string;
            label?: string;
            uriPrefix?: string;
            personReference?: unknown;
            reference?: unknown;
            ref?: unknown;
        };

        const referenceInput = {
            id: candidateWithReference.id,
            label: candidateWithReference.label || fallbackLabel,
            uriPrefix: candidateWithReference.uriPrefix
        };

        if (referenceInput.id) {
            return referenceInput;
        }

        const nextCandidate =
            candidateWithReference.personReference || candidateWithReference.reference || candidateWithReference.ref;

        if (nextCandidate) {
            currentCandidate = nextCandidate;
            continue;
        }

        if (referenceInput.label || referenceInput.uriPrefix) {
            return referenceInput;
        }

        return undefined;
    }

    return undefined;
};

const extractPersonReferenceWithLabel = (option: ReferenceOption) => {
    const label = resolveOptionLabel(option);
    return sanitizePersonReferenceInput(option?.ref, label);
};

const isNonEmptySearchTerm = (value: unknown): boolean => {
    return typeof value === 'string' && value.trim() !== '';
};

export default function AutocompleteController(props: AutocompleteControllerProps) {
    const {
        id,
        name,
        entity,
        inputLabel,
        value,
        onChange,
        additionalFragmentName = undefined,
        multiple = false,
        disabled = false,
        readOnly = false,
        required = false,
        hasErrors = undefined,
        errorText = undefined,
        filter = undefined,
        allowFreeForm = false,
        variant = DEFAULT_WIDGET.defaultWidgetVariant as TextFieldVariants,
        size = DEFAULT_WIDGET.defaultWidgetSize as 'small',
        type = 'autocomplete',
        OptionRenderer = undefined,
        getOption = undefined,
        onCreateHandle = undefined,
        addOption = false,
        query = slimSearchQuery(entity, false, additionalFragmentName),
        saveMutation = undefined,
        preload = true,
        loadOptionsWhenFocused = false,
        filterById = [],
        defaultValueIds = [],
        getDefaultOption = undefined,
        sortBy,
        disableIfOptionUnavailable = false,
        allowDuplicateValue = false,
        notOnlyRef = false,
        testId = undefined,
        tooltip = undefined,
        queryTerm = '',
        exactPhraseMatch = false,
        matchFields = [],
        isAIExtractedData = false,
        requiresUserCorrection = false,
        autoFocus = false,
        skipReferenceCreationOnNoMatch = false,
        returnObjectAsValue = false,
        entityReferenceKey = ENTITY_REFERENCE_LABEL.reference,
        disableCloseOnSelect = false,
        updateAIDataResolved = undefined,
        maxSelectedCount = undefined,
        limitTags = 2,
        draggableChips = false,
        fixedOptions = [],
        filterSelectedOptions = false,
        autoSelectFirstOption = false,
        showInactive = false
    } = props;

    const { activeOrganizationAccount } = useContext(UserContext);
    const { aiExtractedFieldColor } = useStyling();
    const t = useI18n();

    const hasSetDefaultValue = useRef(false);
    const intialMount = useRef(true);
    const hasTriggeredFocusLoad = useRef(false);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = useState<ReferenceOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [disableForNoOption, setDisableForNoOption] = useState(false);
    const autocompleteRef = useRef(null);
    const generatedChipKeysRef = useRef<Map<string, string>>(new Map());
    const generatedChipKeyCounterRef = useRef(0);

    const val = useMemo(() => {
        return getValue(multiple, value, notOnlyRef, returnObjectAsValue, entityReferenceKey);
    }, [entityReferenceKey, multiple, notOnlyRef, returnObjectAsValue, value]);
    const effectiveFilter = useMemo(() => withInactiveFilter(filter, showInactive), [filter, showInactive]);
    const optionFilter = useMemo(() => createOptionFilter(matchFields), [matchFields]);
    const shouldTokenize = matchFields.length > 0;

    // When enabled, Temporarily disable the control and show a spinner until the first option
    // is programmatically selected. This prevents user interaction during the transition from an
    // empty state to the auto-selected value.
    const hasAutoSelectedFirstOption = useRef(false);
    const shouldDisableUntilAutoSelect =
        autoSelectFirstOption && !val && !multiple && !hasAutoSelectedFirstOption.current && loading;

    const getChipKeys = useMemo(() => {
        return makeGetChipKeys(generatedChipKeysRef.current, () => `generated:${generatedChipKeyCounterRef.current++}`);
    }, []);

    const getChipKeyAtIndex = useCallback(
        (selectedOptions: ReferenceOption[], index: number) => {
            const keys = getChipKeys(selectedOptions);
            return keys[index] || `chip-${index}`;
        },
        [getChipKeys]
    );

    const handleChange = useCallback(
        (selectedValue: ReferenceOption | ReferenceOption[]) => {
            const toNameValue = (label: string) => {
                const [firstName = '', ...remaining] = (label || '').trim().split(' ');
                return {
                    firstName,
                    lastName: remaining.join(' ')
                };
            };

            const toNameInputValue = (
                name: { firstName?: string; lastName?: string } | string | undefined,
                label: string
            ) => {
                if (name && typeof name === 'object') {
                    const firstName = name.firstName || '';
                    const lastName = name.lastName || '';
                    if (firstName || lastName) {
                        return {
                            firstName,
                            lastName
                        };
                    }
                }

                if (typeof name === 'string' && name.trim()) {
                    return toNameValue(name);
                }

                return toNameValue(label);
            };

            const toStoredReferenceValueLegacy = () => {
                if (!selectedValue) {
                    return multiple ? [] : undefined;
                }

                if (notOnlyRef || returnObjectAsValue) {
                    return optionToEntity(selectedValue, multiple);
                }

                return optionToReference(selectedValue, multiple);
            };

            const normalizePersonContacts = (selectedContacts: ReferenceOption[]) => {
                return selectedContacts.map((option) => {
                    const optionWithPerson = option as ReferenceOption & {
                        name?: { firstName?: string; lastName?: string } | string;
                        personReference?: any;
                        reference?: any;
                        contactInfo?: any;
                        organization?: string;
                        role?: string;
                        production?: string;
                        productionRole?: string;
                    };
                    const label = resolveOptionLabel(option);
                    const personReference =
                        extractPersonReferenceWithLabel(option) ||
                        extractPersonReferenceWithLabel({ ...option, ref: optionWithPerson.personReference }) ||
                        extractPersonReferenceWithLabel({ ...option, ref: optionWithPerson.reference });

                    return {
                        name: toNameInputValue(optionWithPerson.name, label),
                        contactInfo: optionWithPerson.contactInfo,
                        organization: optionWithPerson.organization,
                        role: optionWithPerson.role,
                        production: optionWithPerson.production,
                        productionRole: optionWithPerson.productionRole,
                        personReference
                    };
                });
            };

            const toStoredReferenceValue = () => {
                const isPersonContactsSelection =
                    multiple && entity === 'Person' && name === 'contacts' && Array.isArray(selectedValue);

                if (isPersonContactsSelection) {
                    return normalizePersonContacts(selectedValue);
                }

                return toStoredReferenceValueLegacy();
            };

            onChange(name, toStoredReferenceValue());
        },
        [entity, multiple, name, notOnlyRef, onChange, returnObjectAsValue]
    );

    const updateOptions = useCallback(
        (term, force = false, filter, isUserInitiatedSearch = true) => {
            const hasSearchTerm = isNonEmptySearchTerm(term);
            if (force || !preload || (isUserInitiatedSearch && hasSearchTerm)) {
                setOptions([]);
                setLoading(true);
                searchClient
                    .query({
                        query: query,
                        variables: {
                            entity,
                            query: exactPhraseMatch && hasSearchTerm ? `"${term}"` : term,
                            page: { from: 0, size: 50 },
                            filter,
                            sortBy,
                            ...(shouldTokenize ? { tokenize: true } : {})
                        },
                        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                        context: {
                            headers: {
                                ownerId: activeOrganizationAccount
                            }
                        }
                    })
                    .then((res) => {
                        if (res && res.data.search.hits && res.data.search.hits.items) {
                            const items = res.data.search.hits.items;
                            if (items) {
                                const opts = items.map((item) => {
                                    const resolvedReference =
                                        item?.[entityReferenceKey] || item?.reference || item?.personReference;

                                    return getOption
                                        ? getOption(item)
                                        : returnObjectAsValue
                                          ? entityToOption(item, resolvedReference)
                                          : {
                                                id: item.id,
                                                label: item.name,
                                                ref: item.reference,
                                                inActive: item?.inActive
                                            };
                                });
                                setOptions(opts);
                                disableIfOptionUnavailable && !items.length && setDisableForNoOption(true);
                                const defaultOption = getDefaultOption ? getDefaultOption(items) : null;
                                if (defaultOption && intialMount.current) {
                                    setDefaultValueById([defaultOption.id], multiple, opts, handleChange);
                                    intialMount.current = false;
                                }
                                // Only set default value once for defaultValueIds
                                if (defaultValueIds.length && !hasSetDefaultValue.current) {
                                    setDefaultValueById(defaultValueIds, multiple, opts, handleChange);
                                    hasSetDefaultValue.current = true;
                                }
                            }
                        }
                    })
                    .catch((err) => {
                        console.error('ReferenceAutocomplete load error:', err);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
        [
            activeOrganizationAccount,
            defaultValueIds,
            disableIfOptionUnavailable,
            entity,
            entityReferenceKey,
            exactPhraseMatch,
            getOption,
            handleChange,
            multiple,
            preload,
            query,
            returnObjectAsValue,
            getDefaultOption,
            sortBy,
            shouldTokenize
        ]
    );

    const updateOptionsRef = useRef(updateOptions);

    useEffect(() => {
        updateOptionsRef.current = updateOptions;
    }, [updateOptions]);

    const updateOptionsForAutocompleteFlow = useCallback(
        (term, force = false, filter, isUserInitiatedSearch = true) => {
            if (loadOptionsWhenFocused && !hasTriggeredFocusLoad.current && !isAIExtractedData) {
                return;
            }

            updateOptions(term, force, filter, isUserInitiatedSearch);
        },
        [isAIExtractedData, loadOptionsWhenFocused, updateOptions]
    );

    const autocompleteFlowUpdateOptions = loadOptionsWhenFocused ? updateOptionsForAutocompleteFlow : updateOptions;

    const {
        isAIExtractedValue,
        setIsAIExtractedValue,
        editingChipKey,
        setEditingChipKey,
        editingChipSearchTerm,
        setEditingChipSearchTerm,
        isAutocompleteOpen,
        setIsAutocompleteOpen,
        isAIFieldActive,
        getChipSx,
        syncUnresolvedChipKeys,
        onAutocompleteOpen,
        onAutocompleteClose,
        onUnresolvedChipClick
    } = useAutocompleteAIFlow({
        multiple,
        isAIExtractedData,
        val,
        name,
        updateAIDataResolved,
        getChipKeys,
        updateOptions: autocompleteFlowUpdateOptions,
        effectiveFilter,
        preload,
        queryTerm,
        requiresUserCorrection,
        aiExtractedFieldColor,
        disableCloseOnSelect,
        setOptions,
        setInputValue
    });

    const handleInputFocus = useCallback(() => {
        if (loadOptionsWhenFocused && !preload && !isAIExtractedData) {
            hasTriggeredFocusLoad.current = true;
            const initialSingleModeSearchTerm =
                !multiple && typeof val === 'string'
                    ? val
                    : !multiple && typeof val === 'object' && val?.label
                      ? val.label
                      : '';

            updateOptionsRef.current(initialSingleModeSearchTerm, true, effectiveFilter);
        }
    }, [effectiveFilter, isAIExtractedData, loadOptionsWhenFocused, multiple, preload, val]);

    const replaceEditedChip = useCallback(
        (selectedOptions: ReferenceOption[]) => {
            return replaceEditedChipSelection({
                multiple,
                editingChipKey,
                currentOptions: Array.isArray(val) ? (val as ReferenceOption[]) : undefined,
                selectedOptions,
                getChipKeys,
                isReferenceOptionEqualToValue: isReferenceOptionEqualById
            });
        },
        [editingChipKey, getChipKeys, multiple, val]
    );

    const replaceEditedChipWithOption = useCallback(
        (selectedOption: ReferenceOption) => {
            return replaceEditedChipWithOptionSelection({
                multiple,
                editingChipKey,
                currentOptions: Array.isArray(val) ? (val as ReferenceOption[]) : undefined,
                selectedOption,
                getChipKeys
            });
        },
        [editingChipKey, getChipKeys, multiple, val]
    );

    const filterOptionsCallback = useCallback(
        (options: ReferenceOption[], params: FilterOptionsState<ReferenceInput>) => {
            const typedInputValue = params.inputValue;
            const userInputValue = deriveUserInputValue({
                typedInputValue,
                multiple,
                editingChipKey,
                editingChipSearchTerm
            });
            const filterInputValue = deriveFilterInputValue({
                userInputValue,
                multiple,
                selectedValue: val
            });
            const effectiveParams = { ...params, inputValue: filterInputValue };

            let filtered: ReferenceOption[] =
                filterById && Array.isArray(filterById) && filterById.length
                    ? optionFilter(options, effectiveParams).filter(
                          (option: ReferenceOption) => !filterById.includes(option.id)
                      )
                    : optionFilter(options, effectiveParams);

            const isExisting = options.some(
                (option) => userInputValue && userInputValue !== '' && userInputValue === resolveOptionLabel(option)
            );

            // Allow free form text?
            const shouldShowFreeFormPlaceholder =
                allowFreeForm &&
                !addOption &&
                userInputValue &&
                (!isExisting || allowDuplicateValue) &&
                !(allowFreeForm && skipReferenceCreationOnNoMatch);

            const toFreeFormOption = (key: string) => {
                filtered.unshift({
                    id: '',
                    label: userInputValue,
                    [key]: {
                        id: '',
                        label: userInputValue
                    }
                });
            };

            if (shouldShowFreeFormPlaceholder) {
                toFreeFormOption(returnObjectAsValue ? entityReferenceKey : 'ref');
            }

            // Suggest creation of a new value.
            const searchTerm = deriveCreateSearchTerm({
                effectiveInputValue: userInputValue,
                multiple,
                addOption,
                isAIExtractedData,
                value,
                allowFreeForm,
                skipReferenceCreationOnNoMatch
            });

            const toCreateOption = (referenceKey: string) => {
                filtered.push(generateCreateOption(searchTerm, referenceKey));
            };

            const hasExactMatchForSearchTerm = hasExactLabelMatch(options, searchTerm);

            if (addOption && searchTerm !== '' && !hasExactMatchForSearchTerm) {
                toCreateOption(returnObjectAsValue ? entityReferenceKey : 'ref');
            }

            return filtered;
        },
        [
            filterById,
            allowFreeForm,
            allowDuplicateValue,
            skipReferenceCreationOnNoMatch,
            addOption,
            isAIExtractedData,
            value,
            multiple,
            val,
            editingChipKey,
            editingChipSearchTerm,
            returnObjectAsValue,
            entityReferenceKey,
            optionFilter
        ]
    );

    const renderOption = useCallback(
        (props, option) => {
            const optionMatchTerm = inputValue || (multiple && editingChipKey ? editingChipSearchTerm : '');
            const isInactiveOption = !!option?.inActive && !option?.createOption;
            const resolvedLabel = resolveOptionLabel(option);
            const label = option.createOption
                ? `${t('form.add')} ${resolvedLabel}`
                : OptionRenderer
                  ? resolvedLabel
                  : markMatch(
                        {
                            ...option,
                            label: resolvedLabel + (isInactiveOption ? ` (${t('fieldDefinition.inactive')})` : '')
                        },
                        optionMatchTerm
                    );
            const subLabel = option.subLabel;
            return (
                <li {...props} key={option.id}>
                    {OptionRenderer ? (
                        <OptionRenderer option={option} />
                    ) : (
                        <ListItemText
                            primary={label}
                            secondary={subLabel}
                            slotProps={isInactiveOption ? { primary: { sx: { fontStyle: 'italic' } } } : undefined}
                        />
                    )}
                </li>
            );
        },
        [editingChipKey, editingChipSearchTerm, inputValue, multiple, OptionRenderer, t]
    );

    const renderInput = useCallback(
        (params) => {
            return (
                <TextField
                    {...params}
                    disabled={disabled || disableForNoOption || shouldDisableUntilAutoSelect} // Keep the field disabled while auto-select-first-option is pending.
                    required={required}
                    variant={variant}
                    label={inputLabel}
                    error={hasErrors}
                    helperText={errorText}
                    sx={AIReferenceFieldStyling(val, isAIFieldActive, aiExtractedFieldColor, requiresUserCorrection)}
                    color={isAIFieldActive ? 'warning' : 'primary'}
                    onFocus={handleInputFocus}
                    onKeyDown={(e) => {
                        if (!multiple && (e.key === 'Backspace' || e.key === 'Delete')) {
                            onChange(name, undefined);
                        }
                    }}
                    InputProps={{
                        ...params.InputProps,
                        size: size,
                        startAdornment:
                            (multiple ? Array.isArray(val) && val.length > 0 : checkForObject(val)) &&
                            isAIFieldActive ? (
                                <>
                                    <AIAdornment requiresUserCorrection={requiresUserCorrection} />
                                    {params.InputProps.startAdornment}
                                </>
                            ) : (
                                params.InputProps.startAdornment
                            ),
                        // Show a small spinner inside the TextField while waiting for the auto-select to complete.
                        endAdornment: (
                            <>
                                {shouldDisableUntilAutoSelect && (
                                    <Loading sx={{ justifyContent: 'flex-end' }} size="16px" />
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            );
        },
        [
            aiExtractedFieldColor,
            disableForNoOption,
            disabled,
            errorText,
            hasErrors,
            inputLabel,
            isAIFieldActive,
            requiresUserCorrection,
            handleInputFocus,
            multiple,
            name,
            onChange,
            required,
            size,
            val,
            variant,
            shouldDisableUntilAutoSelect
        ]
    );

    const triggerNonAISearchAfterSelection = useCallback(
        (reason: string, selectedOption?: unknown) => {
            if (isAIExtractedData || reason !== 'selectOption') {
                return;
            }

            if (multiple) {
                if (disableCloseOnSelect) {
                    return;
                }
                // Avoid clearing + refetching on every multi-select pick when the user
                // didn't type a search term (we likely already have a loaded list).
                if (inputValue.trim()) {
                    updateOptions('', true, effectiveFilter, true);
                }
                return;
            }

            const selectedLabel =
                selectedOption && typeof selectedOption === 'object' && 'label' in selectedOption
                    ? String((selectedOption as { label?: string }).label || '')
                    : '';
            updateOptions(selectedLabel, true, effectiveFilter, true);
        },
        [disableCloseOnSelect, effectiveFilter, inputValue, isAIExtractedData, multiple, updateOptions]
    );

    const onAutocompleteChange = useCallback(
        (e, newValue, reason, details) => {
            const wasClick = e?.type === USER_ACTIONS.click;
            const isExplicitResolveReason =
                reason === 'selectOption' || reason === 'removeOption' || reason === 'clear';

            if (!multiple && reason === 'clear') {
                updateOptions('', true, effectiveFilter, true);
            }

            if (addOption && newValue?.createOption && wasClick) {
                const promise = onCreateHandle(newValue, saveMutation);
                if (promise && typeof promise.then === 'function') {
                    promise.then((createdValue) => {
                        setOptions([...options, createdValue]);
                        if (isAIExtractedValue && !multiple) {
                            setIsAIExtractedValue(false);
                            setOptions([]);
                        }

                        if (multiple && Array.isArray(val)) {
                            const selectedOptions = editingChipKey
                                ? replaceEditedChipWithOption(createdValue)
                                : [...val, createdValue];
                            handleChange(selectedOptions);
                            syncUnresolvedChipKeys(selectedOptions);
                            setEditingChipKey(null);
                            setEditingChipSearchTerm('');
                            setInputValue('');
                            return;
                        }

                        handleChange(createdValue);
                    });
                    promise.catch((err) => {
                        console.error('ReferenceAutocomplete create option error:', err);
                        if (multiple && editingChipKey) {
                            setIsAutocompleteOpen(true);
                        }
                    });
                }
                return;
            }

            if (addOption && Array.isArray(newValue) && newValue.length) {
                const createOptionIndex = newValue.findIndex((selectedOption) => selectedOption.createOption);

                if (createOptionIndex > -1) {
                    const selectedWithoutCreateOption = [...newValue];
                    const createOptionValue = selectedWithoutCreateOption.splice(createOptionIndex, 1)[0];
                    // Optimistically show newly typed chips in both regular and AI flows.
                    // Do not do this when resolving an existing prepopulated chip (editingChipKey set).
                    const shouldOptimisticallyAddChip = multiple && (!isAIFieldActive || !editingChipKey);

                    if (shouldOptimisticallyAddChip) {
                        handleChange([...selectedWithoutCreateOption, createOptionValue]);
                    }

                    const promise = onCreateHandle(newValue[createOptionIndex], saveMutation);
                    if (promise && typeof promise.then === 'function') {
                        promise.then((createdValue) => {
                            setOptions([...options, createdValue]);

                            if (!multiple) {
                                handleChange(createdValue);
                                if (isAIExtractedValue) {
                                    setIsAIExtractedValue(false);
                                    setOptions([]);
                                }
                                return;
                            }

                            const selectedOptions = editingChipKey
                                ? replaceEditedChipWithOption(createdValue)
                                : [...selectedWithoutCreateOption, createdValue];

                            handleChange(selectedOptions);
                            syncUnresolvedChipKeys(selectedOptions);
                            setEditingChipKey(null);
                            setEditingChipSearchTerm('');
                            setInputValue('');
                        });
                        promise.catch((err) => {
                            console.error('ReferenceAutocomplete create option error:', err);
                            if (shouldOptimisticallyAddChip) {
                                handleChange(selectedWithoutCreateOption);
                            }
                            if (multiple && editingChipKey) {
                                setIsAutocompleteOpen(true);
                            }
                        });
                    }
                    return;
                }

                if (multiple) {
                    const selectedOptions =
                        isExplicitResolveReason && reason === 'selectOption' && editingChipKey && details?.option
                            ? replaceEditedChipWithOption(details.option)
                            : isExplicitResolveReason
                              ? replaceEditedChip(newValue)
                              : newValue;
                    handleChange(selectedOptions);
                    if (isExplicitResolveReason) {
                        syncUnresolvedChipKeys(selectedOptions);
                    }

                    if (reason === 'selectOption') {
                        setEditingChipKey(null);
                        setEditingChipSearchTerm('');
                        setInputValue('');
                    }

                    triggerNonAISearchAfterSelection(reason, details?.option);
                    return;
                }

                if (!isAIExtractedValue) {
                    handleChange(newValue);
                    triggerNonAISearchAfterSelection(reason, newValue);
                }
                return;
            }

            if (allowFreeForm && typeof newValue === 'string') {
                const matchedOption = options.find((option) => newValue && newValue === resolveOptionLabel(option));

                if (matchedOption && skipReferenceCreationOnNoMatch) {
                    handleChange({ id: matchedOption.id, label: matchedOption.label, ref: matchedOption.ref });
                } else {
                    if (value && typeof value === 'object' && value.label === newValue) {
                        setInputValue(value.label);
                        return;
                    }

                    handleChange({
                        id: '',
                        label: newValue,
                        ref: skipReferenceCreationOnNoMatch
                            ? null
                            : {
                                  id: '',
                                  label: newValue,
                                  uriPrefix: ''
                              }
                    });
                }
                return;
            }

            let newSelectedOptions = newValue;

            if (multiple && Array.isArray(newValue)) {
                newSelectedOptions =
                    isExplicitResolveReason && reason === 'selectOption' && editingChipKey && details?.option
                        ? replaceEditedChipWithOption(details.option)
                        : isExplicitResolveReason
                          ? replaceEditedChip(newValue)
                          : newValue;
                if (isExplicitResolveReason) {
                    syncUnresolvedChipKeys(newSelectedOptions);
                }
                if (isExplicitResolveReason) {
                    setEditingChipKey(null);
                    setEditingChipSearchTerm('');
                    if (!disableCloseOnSelect || reason === 'clear') {
                        setInputValue('');
                    }
                }
            }

            if (isAIExtractedValue && !multiple) {
                updateAIDataResolved ? updateAIDataResolved(name) : setIsAIExtractedValue(false);
            }

            handleChange(newSelectedOptions);
            triggerNonAISearchAfterSelection(reason, details?.option);
        },
        [
            addOption,
            allowFreeForm,
            disableCloseOnSelect,
            editingChipKey,
            effectiveFilter,
            handleChange,
            isAIFieldActive,
            isAIExtractedValue,
            multiple,
            name,
            onCreateHandle,
            options,
            replaceEditedChip,
            replaceEditedChipWithOption,
            saveMutation,
            setEditingChipKey,
            setEditingChipSearchTerm,
            setIsAIExtractedValue,
            setIsAutocompleteOpen,
            skipReferenceCreationOnNoMatch,
            syncUnresolvedChipKeys,
            triggerNonAISearchAfterSelection,
            updateOptions,
            updateAIDataResolved,
            val,
            value
        ]
    );

    const debouncedFunction = useMemo(
        // Keep a stable debounced callback so in-flight typing searches are not canceled by re-renders.
        () =>
            debounce(
                (term, currentFilter, force = false, isUserInitiatedSearch = true) =>
                    updateOptionsRef.current(term, force, currentFilter, isUserInitiatedSearch),
                TIMEOUT_DELAY
            ),
        []
    );

    useEffect(() => {
        if (autocompleteRef.current && autoFocus) {
            const input = autocompleteRef.current.querySelector('input');
            input?.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        return () => {
            debouncedFunction.cancel();
        };
    }, [debouncedFunction]);

    useEffect(() => {
        if (preload) {
            updateOptionsRef.current('', true, effectiveFilter, false);
        }
    }, [effectiveFilter, preload]);

    useEffect(() => {
        // If auto-select is disabled, do nothing
        if (!autoSelectFirstOption) return;

        const hasSelectedValue = val !== '' && val !== undefined && val !== null;

        const shouldSkip =
            hasAutoSelectedFirstOption.current || multiple || isAIExtractedValue || !options.length || hasSelectedValue;

        if (shouldSkip) return;

        hasAutoSelectedFirstOption.current = true;

        const firstOption = options[0];

        handleChange(firstOption);
        setInputValue(firstOption?.label ?? '');
    }, [handleChange, isAIExtractedValue, loading, multiple, options, val, autoSelectFirstOption]);

    const handleInputChange = useCallback(
        (_event, newInputValue, reason: AutocompleteInputChangeReason) => {
            if (multiple && disableCloseOnSelect) {
                if (reason === 'selectOption' || reason === 'removeOption') {
                    return;
                }
                // MUI resets the input after select; keep the typed term while the list stays open.
                // Still allow blur/clear/empty reset so the field can sync when the popup closes.
                if (reason === 'reset' && inputValue) {
                    return;
                }
            }

            const shouldSearchByTyping =
                reason === 'input' ||
                reason === 'clear' ||
                (!multiple && reason === 'reset' && newInputValue !== inputValue);

            if (shouldSearchByTyping) {
                setEditingChipSearchTerm('');
                const hasSearchTerm = isNonEmptySearchTerm(newInputValue);
                const isUserInitiatedSearch = reason !== 'reset';
                debouncedFunction(newInputValue, effectiveFilter, preload && !hasSearchTerm, isUserInitiatedSearch);
            }

            setInputValue(newInputValue);
        },
        [
            debouncedFunction,
            disableCloseOnSelect,
            effectiveFilter,
            inputValue,
            multiple,
            preload,
            setEditingChipSearchTerm
        ]
    );

    const handleChipReorder = useCallback(
        (reorderedOptions: ReferenceOption[]) => {
            handleChange(reorderedOptions);
        },
        [handleChange]
    );

    const renderTags = useMemo(() => {
        if (!multiple) {
            return undefined;
        }
        if (draggableChips) {
            return (tagValue, getTagProps) => (
                <SortableChips
                    chips={tagValue}
                    getTagProps={getTagProps}
                    fixedOptions={fixedOptions}
                    onReorder={handleChipReorder}
                    getChipKey={(chip, index) => getChipKeyAtIndex(tagValue, index)}
                    getChipSx={getChipSx}
                    onChipClick={onUnresolvedChipClick}
                />
            );
        }
        return (tagValue, getTagProps) =>
            autocompleteCustomRenderMultipleValues({
                value: tagValue,
                tooltip,
                getItemProps: getTagProps,
                getChipKey: (chip, index) => getChipKeyAtIndex(tagValue, index),
                getChipSx,
                onChipClick: onUnresolvedChipClick
            });
    }, [
        draggableChips,
        fixedOptions,
        getChipKeyAtIndex,
        getChipSx,
        onUnresolvedChipClick,
        handleChipReorder,
        multiple,
        tooltip
    ]);

    if (type === INPUT_TYPES.chips) {
        return (
            <ChipOptionsRenderer
                loading={loading}
                options={options}
                value={value}
                label={inputLabel}
                isOptionEqualToValue={isReferenceOptionEqualToValue}
                onChange={handleChange}
            />
        );
    } else if (type === INPUT_TYPES.radio) {
        return (
            <ChipOptionsRenderer
                rendererType={RendererType.RADIO}
                loading={loading}
                options={options}
                value={value}
                label={inputLabel}
                isOptionEqualToValue={isReferenceOptionEqualToValue}
                onChange={handleChange}
            />
        );
    } else {
        return (
            <Autocomplete
                data-testid={testId}
                ref={(ref) => {
                    autocompleteRef.current = ref;
                }}
                onFocus={handleInputFocus}
                size={size}
                disabled={disabled || disableForNoOption || shouldDisableUntilAutoSelect}
                readOnly={readOnly}
                id={id}
                multiple={multiple}
                fullWidth
                disableCloseOnSelect={disableCloseOnSelect}
                autoComplete={false}
                autoSelect={allowFreeForm}
                autoHighlight={!isAIFieldActive}
                freeSolo={true}
                filterSelectedOptions={filterSelectedOptions}
                limitTags={limitTags}
                loading={loading}
                options={options}
                clearOnBlur={!multiple}
                open={isAutocompleteOpen}
                onOpen={onAutocompleteOpen}
                onClose={onAutocompleteClose}
                filterOptions={filterOptionsCallback}
                onInputChange={handleInputChange}
                inputValue={inputValue}
                getOptionLabel={(option: ReferenceOption) => resolveOptionLabel(option)}
                renderOption={renderOption}
                value={val}
                isOptionEqualToValue={isReferenceOptionEqualToValue}
                renderInput={renderInput}
                onChange={onAutocompleteChange}
                renderTags={renderTags}
                // Disable selecting more options when maxSelectedCount is reached
                getOptionDisabled={(option) =>
                    multiple && typeof maxSelectedCount === 'number' && Array.isArray(val)
                        ? val.length >= maxSelectedCount && !val.some((v) => isReferenceOptionEqualToValue(option, v))
                        : false
                }
            />
        );
    }
}
